import React, { useState, useEffect, useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import { getDashboardStats } from '../services/dashboardService';

// Material UI components
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Alert,
  useTheme
} from '@mui/material';

// Material UI icons
import {
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Add as AddIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Sync as SyncIcon,
  Undo as UndoIcon,
  MoreVert as MoreVertIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const theme = useTheme();
  const [stats, setStats] = useState({
    totalParts: 0,
    lowStockParts: 0,
    totalSuppliers: 0,
    totalLocations: 0,
    recentTransactions: [],
    lowStockPartsList: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        const dashboardStats = await getDashboardStats();
        setStats(dashboardStats);

        setLoading(false);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data');
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Helper function to get transaction icon based on type
  const getTransactionIcon = (type) => {
    switch (type) {
      case 'receive':
        return <ArrowUpwardIcon sx={{ color: theme.palette.success.main }} />;
      case 'issue':
        return <ArrowDownwardIcon sx={{ color: theme.palette.error.main }} />;
      case 'adjust':
        return <SyncIcon sx={{ color: theme.palette.warning.main }} />;
      case 'return':
        return <UndoIcon sx={{ color: theme.palette.info.main }} />;
      default:
        return <MoreVertIcon />;
    }
  };

  // Helper function to get transaction color based on type
  const getTransactionColor = (type) => {
    switch (type) {
      case 'receive':
        return 'success';
      case 'issue':
        return 'error';
      case 'adjust':
        return 'warning';
      case 'return':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Dashboard
        </Typography>
        <Grid container spacing={3}>
          {/* Skeleton for stat cards */}
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item}>
              <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 140 }}>
                <Skeleton variant="text" width="60%" height={40} />
                <Skeleton variant="text" width="40%" height={60} />
                <Skeleton variant="text" width="80%" height={20} />
              </Paper>
            </Grid>
          ))}

          {/* Skeleton for recent activity */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
              <Skeleton variant="text" width="40%" height={40} />
              <Skeleton variant="rectangular" width="100%" height={200} />
            </Paper>
          </Grid>

          {/* Skeleton for quick actions */}
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
              <Skeleton variant="text" width="40%" height={40} />
              <Skeleton variant="rectangular" width="100%" height={200} />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Dashboard
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          variant="outlined"
          onClick={() => window.location.reload()}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Overview */}
      <Grid
        container
        spacing={3}
        sx={{
          mb: 3,
          justifyContent: { xs: 'center', md: 'space-between' },
          '& > .MuiGrid-item': {
            maxWidth: { xs: '100%', md: '23%' }
          }
        }}
      >
        <Grid item xs={12} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '4px',
                bgcolor: 'primary.main',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
              <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                <InventoryIcon />
              </Avatar>
              <Button
                component={RouterLink}
                to="/inventory"
                size="small"
                sx={{ alignSelf: 'flex-start', mt: 'auto' }}
              >
                <Typography variant="h5" color="text.secondary" sx={{ mb: 0 }}>
                  {'Total Parts - ' + stats.totalParts}
                </Typography>
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '4px',
                bgcolor: stats.lowStockParts > 0 ? 'warning.main' : 'success.main',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
              <Avatar sx={{ bgcolor: stats.lowStockParts > 0 ? 'warning.light' : 'success.light', mr: 2 }}>
                <WarningIcon />
              </Avatar>
              <Button
                component={RouterLink}
                to="/inventory"
                state={{ showLowStock: true }}
                size="small"
                sx={{ alignSelf: 'flex-start', mt: 'auto' }}
              >
                <Typography variant="h5" color="text.secondary" sx={{ mb: 0 }}>
                  {'Low Stock - ' + stats.lowStockParts}
                </Typography>
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '4px',
                bgcolor: 'info.main',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
              <Avatar sx={{ bgcolor: 'info.light', mr: 2 }}>
                <BusinessIcon />
              </Avatar>
              <Button
                component={RouterLink}
                to="/suppliers"
                size="small"
                sx={{ alignSelf: 'flex-start', mt: 'auto' }}
              >
                <Typography variant="h5" color="text.secondary" sx={{ mb: 0 }}>
                  {'Suppliers - ' + stats.totalSuppliers}
                </Typography>
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={3}>
          <Paper
            elevation={2}
            sx={{
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '4px',
                bgcolor: 'success.main',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
              <Avatar sx={{ bgcolor: 'success.light', mr: 2 }}>
                <LocationIcon />
              </Avatar>
              <Button
                component={RouterLink}
                to="/locations"
                size="small"
                sx={{ alignSelf: 'flex-start', mt: 'auto' }}
              >
                <Typography variant="h5" color="text.secondary" sx={{ mb: 0 }}>
                  {'Locations - ' + stats.totalLocations}
                </Typography>
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Recent Activity */}
        <Grid item xs={12} md={6} sx={{ flexGrow: 1 }}>
          <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', height: '100%' }}>
            <CardHeader
              title="Recent Activity"
              sx={{
                bgcolor: 'grey.50',
                borderBottom: `1px solid ${theme.palette.divider}`,
                pb: 1
              }}
            />
            <CardContent sx={{ p: 0  }}>
              {stats.recentTransactions && stats.recentTransactions.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Part</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell>User</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats.recentTransactions.map(transaction => (
                        <TableRow key={transaction.id} hover>
                          <TableCell>
                            {new Date(transaction.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <RouterLink
                              to={`/inventory/${transaction.part.id}`}
                              style={{
                                color: theme.palette.primary.main,
                                textDecoration: 'none'
                              }}
                            >
                              {transaction.part.internal_part_number}
                            </RouterLink>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={transaction.transaction_type}
                              color={getTransactionColor(transaction.transaction_type)}
                              icon={getTransactionIcon(transaction.transaction_type)}
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {transaction.quantity_change}
                          </TableCell>
                          <TableCell>{transaction.user.username}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No recent activity to display.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Paper>
        </Grid>

        {/* Low Stock Items */}
        <Grid item xs={12} md={6} sx={{ flexGrow: 1 }}>
          <Paper
            elevation={2}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              height: '100%',
              border: stats.lowStockPartsList?.length > 0 ? `1px solid ${theme.palette.warning.light}` : 'none'
            }}
          >
            <CardHeader
              title="Low Stock Items"
              sx={{
                bgcolor: stats.lowStockPartsList?.length > 0 ? 'warning.light' : 'grey.50',
                color: stats.lowStockPartsList?.length > 0 ? 'warning.contrastText' : 'text.primary',
                borderBottom: `1px solid ${stats.lowStockPartsList?.length > 0 ? theme.palette.warning.main : theme.palette.divider}`,
                pb: 1
              }}
            />
            <CardContent sx={{ p: 0 }}>
              {stats.lowStockPartsList?.length > 0 ? (
                <>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Part Number</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell align="right">Current</TableCell>
                          <TableCell align="right">Reorder Point</TableCell>
                          <TableCell>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.lowStockPartsList.slice(0, 5).map(part => (
                          <TableRow key={part.id} hover>
                            <TableCell>{part.internal_part_number}</TableCell>
                            <TableCell>{part.description}</TableCell>
                            <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                              {part.current_quantity}
                            </TableCell>
                            <TableCell align="right">{part.reorder_point}</TableCell>
                            <TableCell>
                              <Button
                                component={RouterLink}
                                to={`/inventory/${part.id}`}
                                size="small"
                                variant="outlined"
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {stats.lowStockPartsList.length > 5 && (
                    <Box sx={{ p: 2, textAlign: 'right' }}>
                      <Button
                        component={RouterLink}
                        to="/inventory"
                        state={{ showLowStock: true }}
                        size="small"
                        endIcon={<WarningIcon />}
                        color="warning"
                      >
                        View all {stats.lowStockPartsList.length} low stock items
                      </Button>
                    </Box>
                  )}
                </>
              ) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No low stock items to display.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Paper>
        </Grid>
      </Grid>

      {/* Quick Actions and Reports */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={5} sx={{ flexGrow: 1 }}>
          <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
            <CardHeader
              title="Quick Actions"
              sx={{
                bgcolor: 'grey.50',
                borderBottom: `1px solid ${theme.palette.divider}`,
                pb: 1
              }}
            />
            <CardContent>
              <Grid
                container
                spacing={2}
                sx={{
                  justifyContent: { xs: 'center', sm: 'flex-start', md: 'space-between' },
                  '& .MuiButton-root': {
                    width: '100%'
                  }
                }}
              >
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    fullWidth
                    component={RouterLink}
                    to="/inventory/new"
                    startIcon={<AddIcon />}
                  >
                    Add New Part
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    component={RouterLink}
                    to="/inventory/adjust"
                    startIcon={<SyncIcon />}
                  >
                    Adjust Inventory
                  </Button>
                </Grid>
                {user && (user.role === 'purchaser' || user.role === 'manager') && (
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      color="secondary"
                      fullWidth
                      component={RouterLink}
                      to="/inventory/reorder-bulk"
                      startIcon={<FilterListIcon />}
                    >
                      Manage Reorder Points
                    </Button>
                  </Grid>
                )}
                {user && (user.role === 'purchaser' || user.role === 'manager') && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Button
                        variant="outlined"
                        fullWidth
                        component={RouterLink}
                        to="/suppliers"
                        startIcon={<BusinessIcon />}
                      >
                        Manage Suppliers
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Button
                        variant="outlined"
                        fullWidth
                        component={RouterLink}
                        to="/locations"
                        startIcon={<LocationIcon />}
                      >
                        Manage Locations
                      </Button>
                    </Grid>
                  </>
                )}
                {user && user.role === 'manager' && (
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      fullWidth
                      component={RouterLink}
                      to="/users"
                      startIcon={<PeopleIcon />}
                    >
                      Manage Users
                    </Button>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Paper>

          <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <CardHeader
              title="Reports"
              sx={{
                bgcolor: 'grey.50',
                borderBottom: `1px solid ${theme.palette.divider}`,
                pb: 1
              }}
              avatar={
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <AssessmentIcon />
                </Avatar>
              }
            />
            <List>
              <ListItem
                button
                component={RouterLink}
                to="/reports/inventory"
                divider
              >
                <ListItemText
                  primary="Inventory Report"
                  secondary="View complete inventory status"
                />
              </ListItem>
              <ListItem
                button
                component={RouterLink}
                to="/reports/reorder"
                divider
              >
                <ListItemText
                  primary="Reorder Points Report"
                  secondary="Track items that need reordering"
                />
              </ListItem>
              <ListItem
                button
                component={RouterLink}
                to="/reports/history"
                divider
              >
                <ListItemText
                  primary="Transaction History Report"
                  secondary="View all inventory movements"
                />
              </ListItem>
              <ListItem
                button
                component={RouterLink}
                to="/reports/supplier-matrix"
              >
                <ListItemText
                  primary="Supplier Matrix Report"
                  secondary="View parts and their suppliers in matrix format"
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
